<script lang="ts">
/**
 * Module-scoped (shared across every Dialog instance, not per-component) open-dialog counter. While
 * at least one Dialog is open, the app root (#app) is made `inert` so focus — via Tab, a click, or a
 * programmatic .focus() call — can never land on page content behind the scrim, regardless of where
 * it started. This is the primary trap mechanism; the Tab-key wrap below is a secondary safety net
 * for browsers/cases where `inert` support is imperfect. A counter (rather than a plain boolean)
 * keeps this correct if a dialog is ever opened from inside another one.
 */
let openDialogCount = 0;

function pushDialogOpen() {
  openDialogCount += 1;
  const app = document.getElementById('app');
  if (app) app.inert = true;
}

function popDialogOpen() {
  openDialogCount = Math.max(0, openDialogCount - 1);
  if (openDialogCount === 0) {
    const app = document.getElementById('app');
    if (app) app.inert = false;
  }
}
</script>

<script setup lang="ts">
/**
 * design-system.md -> Components -> Dialog. 520px wide (override with `width`), radius 16,
 * `shadow-overlay` over a scrim. Holds a mono overline (`overline` prop or slot, e.g. "NEW TENANT"),
 * a close icon button, the dialog title, one line of intro, the body (default slot), and a `sunken`
 * footer (`footer` slot) with Cancel + the primary action.
 *
 * Teleported to <body>. Traps focus while open (see the module script above for the #app `inert`
 * mechanism), closes on Escape, and returns focus to whatever was focused before it opened —
 * including when `open` is already true on mount, and including if the component is unmounted
 * while still open. `role="dialog" aria-modal="true"`, labelled by the title.
 *
 * Initial focus, in order: the first `[autofocus]` element anywhere in the dialog; else the first
 * input/select/textarea in the body slot; else the first other focusable element (body or footer);
 * else, only if truly nothing else is focusable, the close button.
 *
 * v2 additions (mail-admin-handoff-v2-changes -> Find tenant palette), both backwards-compatible —
 * every existing caller keeps passing `title` and gets the exact same markup as before:
 *  - `placement` ("center", default, or "top" — 112px from the viewport top, used by the palette).
 *  - Omitting `title` switches to "chromeless": no overline/title/intro/close-button header, no
 *    body/footer padding wrapper — the default slot renders directly inside the panel and owns its
 *    own header/body/footer rows (the palette's search row, results and key-hint footer). The panel
 *    is labelled by `ariaLabel` instead of the (absent) title, and there is no standard footer slot.
 *
 * Props: open, width (default "520px"), overline, title?, intro, placement ("center" | "top"), ariaLabel.
 * Slots: overline (replaces the `overline` prop), default (body, or the whole panel when chromeless), footer.
 * Emits: close.
 */
import { computed, nextTick, onBeforeUnmount, useId, useTemplateRef, watch } from 'vue';
import { X } from '@lucide/vue';
import Button from './Button.vue';

const props = withDefaults(
  defineProps<{
    open: boolean;
    width?: string;
    overline?: string;
    title?: string;
    intro?: string;
    placement?: 'center' | 'top';
    ariaLabel?: string;
  }>(),
  { width: '520px', placement: 'center' }
);

const panelStyle = computed(() => ({
  width: props.width,
  maxHeight: props.placement === 'top' ? 'calc(100vh - 112px - 24px)' : 'calc(100vh - 48px)'
}));

const emit = defineEmits<{ close: [] }>();

const titleId = useId();
const panelRef = useTemplateRef<HTMLElement>('panel');
const bodyRef = useTemplateRef<HTMLElement>('body');
const closeButtonRef = useTemplateRef<InstanceType<typeof Button>>('closeButton');
let lastFocused: HTMLElement | null = null;
// Whether *this* instance holds a slot in the shared counter. The watch below runs immediately for
// dialogs that mount closed too, and a closed dialog must never release a slot it didn't take —
// otherwise mounting a closed ConfirmDialog beside an open Dialog would un-inert the page.
let holdsSlot = false;

function focusables(): HTMLElement[] {
  if (!panelRef.value) return [];
  return Array.from(
    panelRef.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  );
}

function focusInitial() {
  const panel = panelRef.value;
  if (!panel) return;

  const autofocusEl = panel.querySelector<HTMLElement>('[autofocus]');
  if (autofocusEl) {
    autofocusEl.focus();
    return;
  }

  const bodyInput = bodyRef.value?.querySelector<HTMLElement>('input, select, textarea');
  if (bodyInput) {
    bodyInput.focus();
    return;
  }

  const closeEl = closeButtonRef.value?.$el as HTMLElement | undefined;
  const rest = focusables().filter((el) => el !== closeEl);
  (rest[0] ?? focusables()[0])?.focus();
}

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      lastFocused = document.activeElement as HTMLElement | null;
      pushDialogOpen();
      holdsSlot = true;
      await nextTick();
      focusInitial();
    } else if (holdsSlot) {
      popDialogOpen();
      holdsSlot = false;
      lastFocused?.focus();
      lastFocused = null;
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (holdsSlot) {
    popDialogOpen();
    holdsSlot = false;
    lastFocused?.focus();
    lastFocused = null;
  }
});

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.stopPropagation();
    emit('close');
    return;
  }
  if (event.key !== 'Tab') return;

  const items = focusables();
  const first = items[0];
  const last = items[items.length - 1];
  if (!first || !last) return;

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="dialog-scrim fixed inset-0 z-50 flex justify-center p-6"
      :class="placement === 'top' ? 'items-start' : 'items-center'"
      :style="placement === 'top' ? { paddingTop: '112px' } : undefined"
      @mousedown.self="emit('close')"
      @keydown="onKeydown"
    >
      <section
        ref="panel"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? titleId : undefined"
        :aria-label="!title ? ariaLabel : undefined"
        class="flex flex-col overflow-hidden rounded-dialog bg-surface shadow-overlay"
        :style="panelStyle"
      >
        <template v-if="title">
          <div class="flex flex-col gap-2.5 px-7 pt-6">
            <div class="flex items-center justify-between">
              <span class="type-overline text-ink-3">
                <slot name="overline">{{ overline }}</slot>
              </span>
              <Button
                ref="closeButton"
                variant="icon"
                size="sm"
                aria-label="Close"
                class="!border-0 !bg-transparent text-ink-2"
                @click="emit('close')"
              >
                <X :size="18" :stroke-width="1.75" />
              </Button>
            </div>
            <h2 :id="titleId" class="type-dialog">{{ title }}</h2>
            <p v-if="intro" class="type-body pr-3 text-ink-2">{{ intro }}</p>
          </div>

          <div ref="body" class="overflow-y-auto px-7 pt-6 pb-7">
            <slot />
          </div>

          <div v-if="$slots.footer" class="flex items-center justify-end gap-2.5 border-t border-line-soft bg-sunken px-7 py-4">
            <slot name="footer" />
          </div>
        </template>

        <!-- Chromeless: the caller's default slot is the entire panel body (its own header/body/
             footer rows) — see the module comment. -->
        <slot v-else />
      </section>
    </div>
  </Teleport>
</template>
